import React, { useEffect, useState } from 'react';

/* External */
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';

/* Material UI */
import Grid from '@material-ui/core/Grid';
import { Container, withStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import TextField from '@material-ui/core/TextField';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CardHeader from '@material-ui/core/CardHeader';
import Menu from '@material-ui/core/Menu';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';

/* Project */
import { selectState, actionProps } from 'utils/reduxActions';
import { homeActions } from 'screens/Home/reducer';
import { ReactReduxContext } from 'config/configureStore';
import { clone, truthty, falsy } from 'utils/functions';
import { supplyProductsActions } from 'screens/SupplyProducts/reducer';
import connectSaga from 'utils/saga';
import DeleteModal from 'commons/components/DeleteModal';
import MaintainersPaginator from 'commons/components/MaintainersPaginator';
import TableActionMenu from 'commons/components/TableActionMenu';
import SupplyProductForm from './components/SupplyProductForm';
import saga from './saga';
import styles from './styles';

const useStyles = makeStyles(styles);

function SupplyProducts(props) {
  const {
    objects,
    actions,
    formIsOpen,
    formControls,
    edit,
    invalidControls,
    deleteIsOpen,
    toEditId,
    toDeleteId,
  } = props;

  const { t } = useTranslation();
  const [supplyProductList, setSupplyProductList] = useState([]);
  const [filter, setFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    setSupplyProductList(
      Object.values(objects.supplyProducts).filter((item) =>
        item.name.toLowerCase().includes(filter.toLowerCase()),
      ),
    );
  }, [objects.supplyProducts, filter]);

  useEffect(() => {
    actions.getStateFromApi();
  }, [actions]);

  function handleClick(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function toggleForm() {
    actions.toggleForm('formIsOpen');
  }

  function closeForm() {
    actions.toggleForm('deleteIsOpen');
  }

  function toggleDelete() {
    handleClose();
    actions.toggleDelete(anchorEl.dataset.id);
  }

  function toggleEdit() {
    handleClose();
    const item = Object.values(objects.supplyProducts).find(
      (it) => it.id === Number(anchorEl.dataset.id),
    );
    actions.toggleEdit(item);
  }

  function changeControls(event) {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    actions.changeControls(
      { [event.currentTarget.id]: value },
      { [event.currentTarget.id]: false },
    );
  }

  function submit() {
    if (falsy(formControls.name) || falsy(formControls.unitId)) {
      actions.setInvalidControls({
        name: falsy(formControls.name),
        unitId: falsy(formControls.unitId),
      });
    } else if (edit) {
      actions.editSupplyProduct(toEditId, formControls);
    } else {
      actions.createSupplyProduct(formControls);
    }
  }

  function submitDelete() {
    actions.deleteSupplyProduct(toDeleteId);
  }

  function onFilterChange(event) {
    setFilter(event.target.value);
  }

  const classes = useStyles();

  const getListItem = (supplyProduct, openMenuHandler) => (
    <ListItem key={`supplyProduct-${supplyProduct.id}`}>
      <ListItemAvatar>
        <Avatar className={classes.blue}>{(supplyProduct.name || 'p')[0].toUpperCase()}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={supplyProduct.name}
        secondary={`${t('Unit')}: ${objects.units[supplyProduct.unitId].name} [${
          objects.units[supplyProduct.unitId].code
        }]`}
      />
      <ListItemSecondaryAction>
        <IconButton onClick={openMenuHandler} data-id={supplyProduct.id}>
          <MoreVertIcon />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );

  return (
    <Container maxWidth={false}>
      <Grid container spacing={3} className={classes.container}>
        <Grid item xs={12}>
          <Typography variant="h3" component="h2" className={classes.marginTop}>
            {t('Supply products')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Card className={classes.root}>
            <CardHeader title={t('Filter')} />
            <CardContent>
              <Grid container spacing={3} className={classes.container}>
                <Grid container item xs={4}>
                  <TextField
                    fullWidth
                    id="standard-basic"
                    label={t('Name')}
                    value={filter}
                    onChange={onFilterChange}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid container item>
          <Grid item xs={12} sm={4} md={3}>
            <TableActionMenu
              menuItems={[
                {
                  label: t('Export'),
                  listItems: [
                    {
                      label: t('Excel file'),
                      onClick: actions.exportSupplyProducts,
                      params: [Object.values(objects.supplyProducts)],
                    },
                  ],
                },
              ]}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Card className={classes.root}>
            <CardContent>
              {truthty(objects.supplyProducts) ? (
                <MaintainersPaginator
                  classes={classes}
                  list={supplyProductList}
                  getListItem={getListItem}
                  openMenuHandler={handleClick}
                  closeMenuHandler={handleClose}
                  toggleEdit={toggleEdit}
                  toggleDelete={toggleDelete}
                />
              ) : (
                <Grid container item xs={12} alignItems="center" justifyContent="center">
                  <Typography variant="h6" component="h2">
                    {t('No supply products found')}
                  </Typography>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <SupplyProductForm
        isOpen={formIsOpen}
        toggleForm={toggleForm}
        changeControls={changeControls}
        controls={formControls}
        units={objects.units}
        edit={edit}
        submit={submit}
        invalidControls={invalidControls}
      />
      <DeleteModal
        isOpen={deleteIsOpen}
        toggleForm={closeForm}
        submit={submitDelete}
        buttonId="delete-supplyProduct"
      />
      <Fab color="primary" aria-label="add" className={classes.fab} onClick={toggleForm}>
        <AddIcon />
      </Fab>
      <Menu anchorEl={anchorEl} keepMounted open={truthty(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={toggleEdit}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          {t('Edit')}
        </MenuItem>
        <MenuItem onClick={toggleDelete}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          {t('Delete')}
        </MenuItem>
      </Menu>
    </Container>
  );
}

const withSaga = connectSaga({ key: 'sagaSupplyProducts', saga });

const withConnect = connect(
  selectState(
    'supplyProducts.invalidControls',
    'supplyProducts.formControls',
    'supplyProducts.deleteIsOpen',
    'supplyProducts.toDeleteId',
    'supplyProducts.formIsOpen',
    'supplyProducts.toEditId',
    'supplyProducts.edit',
    'app.objects',
  ),
  actionProps(clone(clone.OBJECT, homeActions, supplyProductsActions)),
  null,
  { context: ReactReduxContext },
);

export default compose(withStyles(styles), withRouter, withConnect)(withSaga(SupplyProducts));
